/**
 * Core image fetching hooks
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Image } from './types';

/**
 * Fetches images from a specific folder or all folders
 */
export const useImages = (folder?: string) => {
  return useQuery({
    queryKey: ['images', folder],
    queryFn: async () => {
      let query = supabase
        .from('images')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (folder && folder !== 'all') {
        query = query.ilike('folder', folder);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Image[];
    },
  });
};

/**
 * Fetches images filtered by tag slugs
 */
export const useImagesByTags = (tagSlugs: string[]) => {
  return useQuery({
    queryKey: ['images', 'by-tags', tagSlugs],
    queryFn: async () => {
      if (tagSlugs.length === 0) return [];

      const { data, error } = await supabase
        .from('images')
        .select(`
          *,
          image_tags!inner(
            tag:tags!inner(slug)
          )
        `)
        .in('image_tags.tag.slug', tagSlugs)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data as Image[];
    },
    enabled: tagSlugs.length > 0,
  });
};
